import { ExternalTransaction_PaymentMethod as ExternalTransactionPaymentMethod } from "gen/accounting/models_pb"
import {
  Account,
  Account_Type as AccountType,
  InternalAccount,
} from "gen/coa/models_pb"

import { paymentMethodIcon } from "lib/banking/PaymentMethodIcon"

export class Accounting {
  static isValidAccountNumber(num: number): boolean {
    const numString = num.toString()

    if (numString.length < 3 || numString.length > 8) {
      return false
    }

    return true
  }

  static accountTypeName(type: AccountType): string {
    const typeToName: Record<AccountType, string> = {
      [AccountType.Asset]: "Asset",
      [AccountType.Liability]: "Liability",
      [AccountType.Equity]: "Equity",
      [AccountType.Income]: "Income",
      [AccountType.COGS]: "COGS",
      [AccountType.Expense]: "Expense",
      [AccountType.Undefined]: "",
    }
    return typeToName[type]
  }

  static externalTransactionPaymentMethodDisplay(
    pm: ExternalTransactionPaymentMethod
  ) {
    const pmToDisplay: Record<
      ExternalTransactionPaymentMethod,
      { text: string; icon: paymentMethodIcon | null }
    > = {
      [ExternalTransactionPaymentMethod.BANK_CARD]: {
        text: "Card",
        icon: "card",
      },
      [ExternalTransactionPaymentMethod.BANK_CASH]: {
        text: "Cash",
        icon: "cash",
      },
      [ExternalTransactionPaymentMethod.BANK_CHECK]: {
        text: "Check",
        icon: "check",
      },
      [ExternalTransactionPaymentMethod.BANK_TRANSFER]: {
        text: "Bank transfer",
        icon: "bank",
      },
      [ExternalTransactionPaymentMethod.UNDEFINED]: {
        text: "None",
        icon: null,
      },
    }
    return pmToDisplay[pm]
  }

  static INTERNAL_ACCOUNT_NAMES: Record<InternalAccount, string> = {
    [InternalAccount.ASSET_CASH_DRAWER]: "Cash drawer",
    [InternalAccount.ASSET_CASH_RUNDOO_BANKING]: "Internal bank account",
    [InternalAccount.ASSET_CASH_EXTERNAL_BANK]: "External bank account",
    [InternalAccount.ASSET_ACCOUNTS_RECEIVABLE]: "Accounts receivable",
    [InternalAccount.ASSET_INTERCOMPANY_RECEIVABLES]:
      "Intercompany receivables",
    [InternalAccount.ASSET_PENDING_CARD_DEPOSITS]: "Card deposits",
    [InternalAccount.ASSET_PENDING_CASH_DEPOSITS]: "Cash deposits",
    [InternalAccount.ASSET_PENDING_CHECK_DEPOSITS]: "Check deposits",
    [InternalAccount.ASSET_INVENTORY]: "Inventory",
    [InternalAccount.ASSET_COMMITTED_INVENTORY]: "Committed inventory",
    [InternalAccount.ASSET_ON_ORDER_INVENTORY]: "On order inventory",
    [InternalAccount.ASSET_NON_STOCK_INVENTORY]: "Non-stock inventory",
    [InternalAccount.ASSET_COMMITTED_NON_STOCK_INVENTORY]:
      "Committed non-stock inventory",
    [InternalAccount.ASSET_ON_ORDER_NON_STOCK_INVENTORY]:
      "On order non-stock inventory",
    [InternalAccount.LIABILITY_ACCOUNTS_PAYABLE]: "Accounts payable",
    [InternalAccount.LIABILITY_SALES_TAX_PAYABLE]: "Sales tax payable",
    [InternalAccount.LIABILITY_UNCATEGORIZED]: "Uncategorized",
    [InternalAccount.LIABILITY_PENDING_ORDERS]: "Pending orders",
    [InternalAccount.LIABILITY_CUSTOMER_DEPOSITS]: "Customer deposits",
    [InternalAccount.EQUITY_RETAINED_EARNINGS]: "Retained earnings",
    [InternalAccount.INCOME_SALES]: "Sales",
    [InternalAccount.INCOME_INTEREST]: "Interest",
    [InternalAccount.INCOME_CARD_SURCHARGE]: "Card surcharge",
    [InternalAccount.INCOME_FORFEITED_DEPOSITS]: "Forfeited deposits",
    [InternalAccount.COGS_INVENTORY_COGS]: "Inventory COGS",
    [InternalAccount.COGS_INVENTORY_ADJUSTMENT]: "Inventory adjustment",
    [InternalAccount.COGS_NON_STOCK_INVENTORY_COGS]: "Non-stock inventory COGS",
    [InternalAccount.COGS_NON_STOCK_INVENTORY_ADJUSTMENT]:
      "Non-stock inventory adjustment",
    [InternalAccount.EXPENSE_PETTY_CASH]: "Petty cash",
    [InternalAccount.EXPENSE_OVER_SHORT]: "Over/Short",
    [InternalAccount.EXPENSE_INVENTORY_TRANSFER]: "Inventory transfer",
    [InternalAccount.EXPENSE_NON_STOCK_INVENTORY_TRANSFER]:
      "Non-stock inventory transfer",
    [InternalAccount.EXPENSE_BAD_DEBT]: "Bad debt",
    [InternalAccount.EXPENSE_CREDIT_CARD_FEES]: "Credit card fees",
    [InternalAccount.EXPENSE_EARLY_PAYMENT_DISCOUNT]: "Early payment discount",
    [InternalAccount.INCOME_ADDITIONAL_FEES]: "Additional fee sales",
    [InternalAccount.EXPENSE_ADDITIONAL_FEES]: "Additional fee expense",
    [InternalAccount.ASSET_CUSTOM_TENDER]: "Custom tender",
    [InternalAccount.INCOME_ACH_SURCHARGE]: "ACH surcharge",
    [InternalAccount.ASSET_PENDING_ACH_DEPOSITS]: "ACH deposits",
    [InternalAccount.EXPENSE_ACH_FEES]: "ACH fees",
    [InternalAccount.INCOME_DISCOUNT]: "Discount",
    [InternalAccount.UNDEFINED]: "",
    [InternalAccount.LIABILITY_GIFT_CARD_DEFFERED_REVENUE]:
      "Gift card deferred revenue",
    [InternalAccount.INCOME_GIFT_CARD_REVENUE]: "Gift card revenue",
    [InternalAccount.EXPENSE_CASH_ROUNDING]: "Cash rounding",
  }
}

export function sortAccount(a?: Account | null, b?: Account | null): number {
  return (a?.number ?? 0).toString().localeCompare((b?.number ?? 0).toString())
}

export function sortedAccounts(accounts: Account[]): Account[] {
  return accounts.toSorted((a, b) => sortAccount(a, b))
}

interface ItemWithAccount {
  account?: Account | null
}

export function sortedItemWithAccount<T extends ItemWithAccount>(
  items: T[]
): T[] {
  return items.toSorted((a, b) => sortAccount(a.account, b.account))
}
